import { Form } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
} from "sipli-fleet-core/components";
import type { TirePresetConfigRow } from "./TirePresetConfig";

interface AddTireConfigModalProps {
  open: boolean;
  initialValues?: TirePresetConfigRow | null;
  onClose: () => void;
  onSubmit: (values: TirePresetConfigRow) => void;
}

type TireConfigFormValues = TirePresetConfigRow;

const defaultValues: TireConfigFormValues = {
  side: "",
  position: "",
  temperature: "",
  pressure: "",
  make: "",
  model: "",
  measure: "",
  attached_unit: "",
};

const AddTireConfigModal = ({
  open,
  initialValues,
  onClose,
  onSubmit,
}: AddTireConfigModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<TireConfigFormValues>();

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue(initialValues ?? defaultValues);
  }, [form, initialValues, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    onSubmit({
      id: values.id ?? initialValues?.id,
      side: values.side.trim(),
      position: values.position.trim(),
      temperature: values.temperature.trim(),
      pressure: values.pressure.trim(),
      make: values.make.trim(),
      makeId: values.makeId ?? initialValues?.makeId,
      model: values.model.trim(),
      modelId: values.modelId ?? initialValues?.modelId,
      measure: values.measure.trim(),
      measureId: values.measureId ?? initialValues?.measureId,
      attached_unit: values.attached_unit.trim(),
      tireKilometers: values.tireKilometers ?? initialValues?.tireKilometers,
      vehicleKilometersAtInstall:
        values.vehicleKilometersAtInstall ?? initialValues?.vehicleKilometersAtInstall,
    });
    form.resetFields();
  };

  const requiredRule = { required: true, whitespace: true, message: t("field_required") };

  return (
    <CustomModal
      open={open}
      title={t(initialValues ? "edit_tire_config" : "add_tire_config")}
      onCancel={handleClose}
      destroyOnHidden
      width={960}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-5 sm:grid-cols-2"
      >
        <Form.Item name="side" label={t("side")} rules={[requiredRule]} className="mb-3!">
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="position"
          label={t("position")}
          rules={[requiredRule]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="temperature"
          label={t("max_temperature")}
          rules={[requiredRule]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="pressure"
          label={t("max_pressure")}
          rules={[requiredRule]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item name="make" label={t("tire_make")} rules={[requiredRule]} className="mb-3!">
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item name="model" label={t("tire_model")} rules={[requiredRule]} className="mb-3!">
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="measure"
          label={t("tire_measure")}
          rules={[requiredRule]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="attached_unit"
          label={t("unit_attached")}
          rules={[requiredRule]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("type")} maxLength={64} />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={initialValues ? "update" : "create"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddTireConfigModal;
